import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import useApiDelete from '@/hooks/useApiDelete';
import useTitle from '@/hooks/useTitle';
import {
  Avatar,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Popper,
  Paper,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { useRouteLoaderData } from 'react-router-dom';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import useAppStore from '@/store/app';
import { AccessTime } from '@mui/icons-material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EmergencyOutlinedIcon from '@mui/icons-material/EmergencyOutlined';
import ListView from '@/components/datagrid/ListView';
import useSearchStore from '@/store/search';
import useApiMutation from '@/hooks/useApiMutation';
import api from '@/services/ApiService';
import qs from 'qs';
import * as XLSX from 'xlsx';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { ApiAggregation } from '@/components/datagrid/columns/ApiAggregation';
import TanssImportDialog from './TanssImportDialog';

export default function TimeEntries() {
  useTitle('Zeitbuchungen');
  const { liveData } = useLiveData('timeRecord');
  const {
    users,
    fixedDeploymentConfigs: { time },
    me: {
      _id: meId,
      tanssEmployeeId = null,
      role: { permissions = [], superuser = false },
    },
  } = useRouteLoaderData('auth');
  const config = time?.modeOptions[time?.selectedMode] ?? null;
  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedItem, setSelectedItem } = useAppStore();

  const canApprove =
    superuser || permissions?.timeRecord?.update?.approvedAt === true;
  const canBill = superuser || permissions?.timeRecord?.update?.billed === true;

  const [billingFilter, setBillingFilter] = useState('unbilled');
  const [approvedFilter, setApprovedFilter] = useState(
    canBill ? 'approved' : 'pending',
  );

  const [typeFilter, setTypeFilter] = useState('all');
  const [isWeekend, setIsWeekend] = useState(false);
  const [showOnlyOwn, setShowOnlyOwn] = useState(false);

  const canSeeAllRecords = canApprove;

  const [importOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      label: 'Zeitbuchungen',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  const mongoFilter = useMemo(() => {
    const filter = { $and: [] };

    if (searchQuery) {
      filter.$and.push({
        $or: [
          { title: { $regex: searchQuery, $options: 'i' } },
          { description: { $regex: searchQuery, $options: 'i' } },
        ],
      });
    }

    if (typeFilter === 'overtime') {
      filter.$and.push({ isOvertime: true });
    } else if (typeFilter === 'emergency') {
      filter.$and.push({ isEmergencyService: true });
    }

    if (isWeekend) {
      filter.$and.push({ isWeekend: true });
    }

    if (approvedFilter === 'approved') {
      filter.$and.push({ approvedAt: { $type: 'date' } });
    } else if (approvedFilter === 'pending') {
      filter.$and.push({
        $or: [
          { approvedAt: { $exists: false } },
          { approvedAt: { $type: 'null' } },
        ],
      });
    }

    if (billingFilter === 'unbilled') {
      filter.$and.push({ billed: { $ne: true } });
    } else if (billingFilter === 'billed') {
      filter.$and.push({ billed: true });
    }

    if (showOnlyOwn || !canSeeAllRecords) {
      filter.$and.push({ user: meId });
    }
    if (filter.$and.length === 0) {
      return {};
    }
    return filter;
  }, [
    searchQuery,
    typeFilter,
    isWeekend,
    approvedFilter,
    billingFilter,
    showOnlyOwn,
    canSeeAllRecords,
    meId,
  ]);

  const { mutateAsync: deleteTimeRecord } = useApiDelete('timeRecord');

  const {
    data: timeRecords,
    isPending,
    refetch,
  } = useApiQuery('timeRecord', mongoFilter, true);

  const [totalTimeData, setTotalTimeData] = useState({
    totalMs: 0,
    loading: true,
  });
  const fetchTotalTime = useCallback(async () => {
    setTotalTimeData((prev) => ({ ...prev, loading: true }));
    try {
      const queryString = qs.stringify(
        { ...mongoFilter, limit: 10000 },
        { encode: true, arrayFormat: 'indices', allowDots: false },
      );
      const {
        data: { result = [] },
      } = await api.get(`/timeRecord?${queryString}`);

      const totalMs = result.reduce((acc, record) => {
        if (record.duration !== undefined && record.duration !== null) {
          return acc + Number(record.duration) * 1000;
        }
        if (record.startDate && record.endDate) {
          return acc + (new Date(record.endDate) - new Date(record.startDate));
        }
        return acc;
      }, 0);

      setTotalTimeData({ totalMs, loading: false });
    } catch (error) {
      console.error('Error fetching total time:', error);
      setTotalTimeData({ totalMs: 0, loading: false });
    }
  }, [mongoFilter]);

  useEffect(() => {
    fetchTotalTime();
  }, [fetchTotalTime]);

  const formattedTotalTime = useMemo(() => {
    const totalMinutes = Math.floor(totalTimeData.totalMs / 1000 / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }, [totalTimeData.totalMs]);

  const { mutateAsync: updateTimeRecord } = useApiMutation('timeRecord');

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      if (selectedItem === id) {
        setSelectedItem(null);
      }
      deleteTimeRecord(id);
    });
    setSelectedRows([]);
  };

  const handleBilling = async () => {
    const toastId = toast.info(
      <ToastMessage
        title="Abrechnung"
        subtitle="Daten werden vorbereitet..."
      />,
      { autoClose: false, closeOnClick: false, isLoading: true },
    );

    try {
      const queryString = qs.stringify(mongoFilter, {
        encode: true,
        arrayFormat: 'indices',
        allowDots: false,
      });

      const {
        data: { result = [] },
      } = await api.get(`/timeRecord?${queryString}`);

      if (result.length === 0) {
        toast.update(toastId, {
          render: (
            <ToastMessage title="Fehler" subtitle="Keine Einträge gefunden." />
          ),
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const uniquePartyIds = [
        ...new Set(result.map((r) => r.partyId).filter(Boolean)),
      ];
      const partyCache = {};
      await Promise.all(
        uniquePartyIds.map(async (id) => {
          try {
            const { data } = await api.get(`/party/${id}`);
            partyCache[id] =
              `${data.company || ''} ${data.company2 || ''}`.trim();
            // eslint-disable-next-line no-unused-vars
          } catch (e) {
            partyCache[id] = 'Unbekannter Kunde';
          }
        }),
      );

      const uniqueLinkedEventIds = [
        ...new Set(
          result
            .filter((r) => !r.partyId && r.linkedEvent)
            .map((r) => r.linkedEvent),
        ),
      ];
      const eventCache = {};
      await Promise.all(
        uniqueLinkedEventIds.map(async (id) => {
          try {
            const { data } = await api.get(`/event/${id}`);
            const companyName = data?.companyFullName
              ? data.companyFullName.replace(/[[\]]/g, '')
              : 'Unbekannter Kunde';
            eventCache[id] = {
              name: companyName,
              tanssCompanyId: data?.links?.tanssCompanyId ?? null,
            };
          } catch (e) {
            eventCache[id] = {
              name: 'Unbekannter Kunde',
              tanssCompanyId: null,
            };
          }
        }),
      );

      const generateAndShowDownload = (currentResults) => {
        const detailedData = currentResults.map((record) => {
          const user = users.find((u) => u._id === record.user);
          const start = record.startDate ? new Date(record.startDate) : null;
          const end = record.endDate ? new Date(record.endDate) : null;
          const durationHours =
            record.duration !== undefined && record.duration !== null
              ? Number(record.duration) / 3600
              : start && end
                ? (end - start) / 1000 / 60 / 60
                : 0;
          const userName =
            `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

          return {
            Name: userName,
            Datum: start ? start.toLocaleDateString('de-DE') : '',
            Arbeitszeitstart: start
              ? start.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '',
            Arbeitszeitende: end
              ? end.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '',
            Dauer: durationHours.toFixed(2).replace('.', ','),
            Beschreibung: record.description || '',
            Wochenendarbeit: record.isWeekend ? 'Ja' : 'Nein',
            Kunde: record.partyId
              ? partyCache[record.partyId] || 'Interner Kunde'
              : record.linkedEvent && eventCache[record.linkedEvent]
                ? `${eventCache[record.linkedEvent].name}${eventCache[record.linkedEvent].tanssCompanyId ? ` (ID: ${eventCache[record.linkedEvent].tanssCompanyId})` : ''}`
                : 'Interner Kunde',
            Ticket: record.ticketReference || '',
            'Buchung verrechnet': record.billed ? 'Ja' : 'Nein',
            'Factorial HR geprüft':
              record.externalStatus === 'CHECKED_OK' ? 'Ja' : 'Nein',
            isOvertime: record.isOvertime,
            isEmergencyService: record.isEmergencyService,
          };
        });

        const overtimeData = detailedData
          .filter((r) => r.isOvertime)
          .map(({ isOvertime, isEmergencyService, ...rest }) => rest);

        const emergencyData = detailedData
          .filter((r) => r.isEmergencyService)
          .map(({ isOvertime, isEmergencyService, ...rest }) => rest);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(overtimeData),
          'Überstunden',
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(emergencyData),
          'Notdienste',
        );

        toast.update(toastId, {
          isLoading: false,
          type: 'success',
          render: (
            <Box fullWidth>
              <ToastMessage
                title="DownloadExcel"
                subtitle="Die Datei steht nun zum Download bereit."
              />
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="small"
                startIcon={<CloudDownloadOutlinedIcon />}
                sx={{ mt: 2 }}
                onClick={() => {
                  XLSX.writeFile(
                    workbook,
                    `Abrechnung_${new Date().toISOString().split('T')[0]}.xlsx`,
                  );
                  toast.dismiss(toastId);
                }}
              >
                Herunterladen
              </Button>
            </Box>
          ),
        });
      };

      if (canBill) {
        toast.update(toastId, {
          isLoading: false,
          type: 'info',
          render: (
            <Box>
              <ToastMessage
                title="Abrechnungs-Optionen"
                subtitle="Möchten Sie die Einträge als ausbezahlt markieren?"
              />
              <Box
                sx={{ mt: 2, display: 'flex', flexDirection: 'row', gap: 1 }}
              >
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CreditScoreOutlinedIcon />}
                  fullWidth
                  size="small"
                  onClick={async () => {
                    toast.update(toastId, {
                      render: <ToastMessage title="Markiere Einträge..." />,
                      isLoading: true,
                    });
                    await Promise.allSettled(
                      result.map((record) =>
                        updateTimeRecord(
                          { billed: true },
                          'timeRecord',
                          record._id,
                          false,
                          true,
                        ),
                      ),
                    );
                    refetch();
                    const updatedResults = result.map((r) => ({
                      ...r,
                      billed: true,
                    }));
                    generateAndShowDownload(updatedResults);
                  }}
                >
                  Abrechnen
                </Button>
                <Button
                  color="default"
                  variant="outlined"
                  size="small"
                  onClick={() => generateAndShowDownload(result)}
                >
                  <RedoOutlinedIcon />
                </Button>
              </Box>
            </Box>
          ),
        });
      } else {
        generateAndShowDownload(result);
      }
    } catch (error) {
      console.error(error);
      toast.update(toastId, {
        render: (
          <ToastMessage title="Fehler" subtitle="Aktion fehlgeschlagen." />
        ),
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const columns = [
    {
      field: 'statusIcon',
      headerName: '',
      align: 'center',
      minWidth: 48,
      maxWidth: 48,
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      renderCell: ({ row }) => {
        if (row?.isOvertime) {
          return (
            <Tooltip title="Überstunde" arrow>
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <TimerOutlinedIcon color="primary" />
              </Box>
            </Tooltip>
          );
        }
        if (row?.isEmergencyService) {
          return (
            <Tooltip title="Notdienst" arrow>
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <EmergencyOutlinedIcon color="error" />
              </Box>
            </Tooltip>
          );
        }
        return null;
      },
    },
    {
      field: 'tanssSupport',
      headerName: '',
      align: 'center',
      minWidth: 48,
      maxWidth: 48,
      renderCell: ({ row }) => {
        if (!config?.tanssSupportUrl || (!row?.tanss && !row?.linkedEvent))
          return null;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Tooltip title="TANSS Support öffnen" arrow>
              <Button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    let tanssId = row.tanss ? Number(row.tanss) : null;

                    if (!tanssId && row.linkedEvent) {
                      const { data } = await api.get(
                        `/event/${row.linkedEvent}`,
                      );
                      tanssId = data?.links?.find(
                        (link) => !!link?.tanssSupportId,
                      )?.tanssSupportId;
                    }

                    if (tanssId && config?.tanssSupportUrl) {
                      const href = `${config.tanssSupportUrl}${tanssId}`;
                      window.open(href, '_blank', 'noopener,noreferrer');
                    } else {
                      toast.error(
                        <ToastMessage
                          title="Fehler"
                          subtitle="Keine tanssSupportId gefunden."
                        />,
                      );
                    }
                  } catch (err) {
                    toast.error(
                      <ToastMessage
                        title="Fehler"
                        subtitle="Konnte Event nicht laden."
                      />,
                    );
                  }
                }}
                variant="text"
                size="small"
              >
                <OpenInNewOutlinedIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'user',
      headerName: 'Mitarbeiter:in',
      align: 'center',
      minWidth: 250,
      renderCell: ({ row }) => {
        const currentUser = users.find((user) => user._id === row.user);
        return (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Avatar
              src={
                currentUser?.profilePicture &&
                `${import.meta.env.VITE_FILE_URL}/${currentUser?.profilePicture}`
              }
              sx={{ width: '32px', height: '32px', mr: 1 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body2">
                {`${currentUser?.firstName} ${currentUser?.lastName}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'partyId',
      headerName: 'Kunde',
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => {
        if (!row.partyId) {
          return (
            <ApiAggregation
              id={row.linkedEvent}
              resource="event"
              mapResponse={(data) =>
                data && `${data.companyFullName.replace(/[\[\]]/g, '') || ''}`
              }
              placeholder="—"
            />
          );
        } else {
          return (
            <ApiAggregation
              id={row.partyId}
              resource="party"
              mapResponse={(data) =>
                data && data?.partyType === 'PERSON'
                  ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
                  : `${data.company || ''}${data.company2 ? ` ${data.company2}` : ''}`.trim()
              }
              placeholder="—"
            />
          );
        }
      },
    },
    {
      field: 'description',
      headerName: 'Beschreibung',
      flex: 2,
    },
    {
      field: 'startDate',
      headerName: 'Geleiset am',
      minWidth: 130,

      valueGetter: (value) =>
        new Date(value).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      field: 'duration',
      headerName: 'Dauer',
      renderCell: ({ row }) => {
        const durationMs =
          row.duration !== undefined && row.duration !== null
            ? Number(row.duration) * 1000
            : row.startDate && row.endDate
              ? new Date(row.endDate) - new Date(row.startDate)
              : 0;
        if (durationMs > 0) {
          const hours = Math.floor(durationMs / 1000 / 60 / 60);
          const minutes = Math.floor((durationMs / 1000 / 60) % 60);
          return `${hours ? `${hours}h ` : ''}${minutes}m`;
        }
        return '';
      },
    },
    ...(canApprove
      ? [
          {
            field: 'externalStatus',
            headerName: 'Prüfstatus',
            minWidth: 180,
            renderHeader: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <img
                  src="https://app.factorialhr.com/favicons/apple-touch-icon.png"
                  alt="Factorial"
                  style={{ width: 18, height: 18, borderRadius: 3 }}
                />
                Prüfstatus
              </Box>
            ),
            renderCell: ({ row }) => {
              const statusMap = {
                '': 'Ungeprüft',
                CHECKED_OK: 'Geprüft, ok',
                CHECKED_FAILED: 'Geprüft, fehlerhaft',
              };
              const currentValue = row.externalStatus ?? '';
              return (
                <Select
                  value={currentValue}
                  displayEmpty
                  renderValue={(val) => statusMap[val] || 'Ungeprüft'}
                  onChange={async (e) => {
                    e.stopPropagation();
                    const value =
                      e.target.value === '' ? null : e.target.value;
                    await updateTimeRecord(
                      { externalStatus: value },
                      'timeRecord',
                      row._id,
                      false,
                      true,
                    );
                    refetch();
                  }}
                  size="small"
                  variant="standard"
                  fullWidth
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="">Ungeprüft</MenuItem>
                  <MenuItem value="CHECKED_OK">Geprüft, ok</MenuItem>
                  <MenuItem value="CHECKED_FAILED">
                    Geprüft, fehlerhaft
                  </MenuItem>
                </Select>
              );
            },
          },
        ]
      : []),
    {
      field: 'approvedAt',
      headerName: 'Genehmigt am',
      minWidth: 130,

      valueGetter: (value) =>
        value
          ? new Date(value).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
    },
    {
      field: 'approvedBy',
      headerName: 'Genehmigt von',
      align: 'center',
      minWidth: 250,
      renderCell: ({ row }) => {
        const currentUser = users.find((user) => user._id === row.approvedBy);
        return (
          currentUser && (
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'row',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Avatar
                src={
                  currentUser?.profilePicture &&
                  `${import.meta.env.VITE_FILE_URL}/${currentUser?.profilePicture}`
                }
                sx={{ width: '32px', height: '32px', mr: 1 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexFlow: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="body2">
                  {`${currentUser?.firstName} ${currentUser?.lastName}`}
                </Typography>
              </Box>
            </Box>
          )
        );
      },
    },
  ];

  const actions = [];
  const filterBtnRef = useRef(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const openFilter = () => setFilterOpen(true);
  const closeFilter = () => setFilterOpen(false);
  const filterActive =
    (billingFilter && billingFilter !== 'all') ||
    (typeFilter && typeFilter !== 'all') ||
    isWeekend ||
    (approvedFilter && approvedFilter !== 'all') ||
    showOnlyOwn;

  actions.push(
    <Chip
      key="total-time"
      icon={
        totalTimeData.loading ? (
          <CircularProgress size={17} color="inherit" />
        ) : (
          <AccessTime />
        )
      }
      label={
        totalTimeData.loading ? 'Berechne...' : `Gesamt: ${formattedTotalTime}`
      }
      variant="outlined"
      color="primary"
      sx={{ alignSelf: 'center' }}
    />,
  );
  if (tanssEmployeeId && config?.tanssImport && config?.iface) {
    actions.push(
      <Button
        key="import-action"
        color="primary"
        onClick={() => setImportOpen(true)}
      >
        Importieren
      </Button>,
    );
  }

  if (
    superuser ||
    (permissions?.timeRecord?.update?.approvedAt === true &&
      permissions?.timeRecord?.update?.approvedBy === true)
  ) {
    actions.push(
      <Button
        disabled={
          !selectedRows.length ||
          selectedRows.every(
            (row) => timeRecords.find((tr) => tr._id === row)?.approvedAt,
          )
        }
        color="primary"
        onClick={async () => {
          if (!selectedRows.length) return;
          const toastId = toast.info(
            <ToastMessage
              title="Genehmigung läuft"
              subtitle="Bitte warten..."
            />,
            { autoClose: false, closeOnClick: false, isLoading: true },
          );
          const results = await Promise.allSettled(
            selectedRows.map((id) => {
              return updateTimeRecord(
                {
                  approvedAt: new Date(),
                  approvedBy: meId,
                },
                'timeRecord',
                id,
                false,
                true,
              );
            }),
          );
          let success = 0;
          let failed = 0;
          let forbidden = 0;
          let otherErrors = [];
          results.forEach((res) => {
            if (res.status === 'fulfilled') {
              success++;
            } else {
              const code =
                res.reason?.response?.data?.code ||
                res.reason?.code ||
                res.reason?.status;
              if (code === 403) {
                forbidden++;
              } else {
                failed++;
                otherErrors.push(
                  res.reason?.message ||
                    res.reason?.response?.data?.message ||
                    'Unbekannter Fehler',
                );
              }
            }
          });
          toast.dismiss(toastId);
          if (forbidden > 0) {
            toast.error(
              <ToastMessage
                title="Keine Rechte"
                subtitle="Du hast keine Berechtigung, um einen oder mehrere Einträge zu genehmigen."
              />,
            );
          }
          if (failed > 0) {
            toast.error(
              <ToastMessage
                title="Fehler beim Genehmigen"
                subtitle={`Bei ${failed} Einträgen ist ein Fehler aufgetreten: ${otherErrors.join(', ')}`}
              />,
            );
          }
          if (success > 0) {
            toast.success(
              <ToastMessage
                title="Genehmigt"
                subtitle={`${success} Einträge erfolgreich genehmigt${failed > 0 ? `, ${failed} fehlgeschlagen` : ''}${forbidden > 0 ? `, ${forbidden} keine Rechte` : ''}.`}
              />,
            );
          }
        }}
      >
        Genehmigen
      </Button>,
    );
  }

  actions.push(
    <Button key="billing-action" color="primary" onClick={handleBilling}>
      Export
    </Button>,
    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      onClick: handleDelete,
    },
    <Box flexGrow={1} />,
    <Box
      key="filter-box"
      onMouseEnter={openFilter}
      onMouseLeave={closeFilter}
      sx={{
        display: 'inline-block',
        position: 'relative',
      }}
    >
      <ToggleButton
        value="filter"
        selected={filterActive}
        aria-label="Filter"
        variant={filterActive ? 'contained' : 'outlined'}
        color={filterActive ? 'success' : 'default'}
        disabled
        sx={{
          height: 32,
          mt: 0.2,
          pt: 1.5,
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'pointer',
          },
        }}
        ref={filterBtnRef}
      >
        Filter
      </ToggleButton>

      <Popper
        open={filterOpen}
        anchorEl={filterBtnRef.current}
        placement="bottom-start"
        onMouseEnter={() => setFilterOpen(true)}
        onMouseLeave={() => setFilterOpen(false)}
      >
        <Paper
          sx={{
            p: 2.3,
            mt: 1,
            minWidth: 320,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontSize="1.3rem"
          >
            Filteroptionen
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="1.1rem"
            >
              Buchungsart
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={typeFilter}
              onChange={(_, newVal) => setTypeFilter(newVal ?? 'all')}
              aria-label="Artfilter"
              size="small"
              sx={{ width: '100%', display: 'flex' }}
            >
              <ToggleButton
                value="overtime"
                variant={typeFilter === 'overtime' ? 'contained' : 'outlined'}
                color={typeFilter === 'overtime' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Überstunden
              </ToggleButton>
              <ToggleButton
                value="all"
                variant={typeFilter === 'all' ? 'contained' : 'outlined'}
                color={typeFilter === 'all' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Alle
              </ToggleButton>
              <ToggleButton
                value="emergency"
                variant={typeFilter === 'emergency' ? 'contained' : 'outlined'}
                color={typeFilter === 'emergency' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Notdienst
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="1.1rem"
            >
              Genehmigungsstatus
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={approvedFilter}
              onChange={(_, newVal) => setApprovedFilter(newVal ?? 'all')}
              aria-label="Genehmigt"
              size="small"
              sx={{ width: '100%', display: 'flex' }}
            >
              <ToggleButton
                value="approved"
                variant={
                  approvedFilter === 'approved' ? 'contained' : 'outlined'
                }
                color={approvedFilter === 'approved' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Genehmigt
              </ToggleButton>
              <ToggleButton
                value="all"
                variant={approvedFilter === 'all' ? 'contained' : 'outlined'}
                color={approvedFilter === 'all' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Alle
              </ToggleButton>
              <ToggleButton
                value="pending"
                variant={
                  approvedFilter === 'pending' ? 'contained' : 'outlined'
                }
                color={approvedFilter === 'pending' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Ausstehend
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="1.1rem"
            >
              Zahlungsstatus
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={billingFilter}
              onChange={(_, newVal) => setBillingFilter(newVal ?? 'all')}
              aria-label="Abrechnung"
              size="small"
              sx={{ width: '100%', display: 'flex' }}
            >
              <ToggleButton
                value="unbilled"
                variant={
                  billingFilter === 'unbilled' ? 'contained' : 'outlined'
                }
                color={billingFilter === 'unbilled' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Unbezahlt
              </ToggleButton>
              <ToggleButton
                value="all"
                variant={billingFilter === 'all' ? 'contained' : 'outlined'}
                color={billingFilter === 'all' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Alle
              </ToggleButton>
              <ToggleButton
                value="billed"
                variant={billingFilter === 'billed' ? 'contained' : 'outlined'}
                color={billingFilter === 'billed' ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Bezahlt
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="1.1rem"
            >
              Wochentag
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={isWeekend ? 'weekend' : 'all'}
              onChange={(_, newVal) =>
                newVal && setIsWeekend(newVal === 'weekend')
              }
              aria-label="Wochenende"
              size="small"
              sx={{ width: '100%', display: 'flex' }}
            >
              <ToggleButton
                value="all"
                variant={!isWeekend ? 'contained' : 'outlined'}
                color={!isWeekend ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Alle
              </ToggleButton>
              <ToggleButton
                value="weekend"
                variant={isWeekend ? 'contained' : 'outlined'}
                color={isWeekend ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Wochenende
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontSize="1.1rem"
            >
              Benutzer
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={showOnlyOwn ? 'own' : 'all'}
              onChange={(_, newVal) =>
                newVal && setShowOnlyOwn(newVal === 'own')
              }
              aria-label="Eigene"
              size="small"
              sx={{ width: '100%', display: 'flex' }}
            >
              <ToggleButton
                value="all"
                variant={!showOnlyOwn ? 'contained' : 'outlined'}
                color={!showOnlyOwn ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Alle
              </ToggleButton>
              <ToggleButton
                value="own"
                variant={showOnlyOwn ? 'contained' : 'outlined'}
                color={showOnlyOwn ? 'primary' : 'default'}
                sx={{ flex: 1, height: 35 }}
              >
                Eigene
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>
      </Popper>
    </Box>,
  );

  return (
    <>
      <ListView
        key={JSON.stringify(mongoFilter)}
        rows={timeRecords || []}
        columns={columns}
        actions={actions}
        loading={isPending}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        refetch={refetch}
        livedata={liveData}
        disableRowSelectionOnClick={false}
      />
      {config?.iface && (
        <TanssImportDialog
          open={importOpen}
          onClose={() => {
            setImportOpen(false);
            refetch();
          }}
          config={config}
        />
      )}
    </>
  );
}
